import currenciesService from '@/services/currencies';
import generalCatalogsService from '@/services/generalCatalogs';
import collaboratorsService from '@/services/collaborators';
import suppliersService from '@/services/suppliers';
import customersService from '@/services/customers';
import salesService from '@/services/sales';
import salesFlatRatesService from '@/services/salesFlatRates';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import areasService from '@/services/areas';
import tagsService from '@/services/tags';
import taxesService from '@/services/taxes';
import { normalizeCatalogOptions, normalizeCurrencyOptions } from '@/utils/catalogOptions';
import { extractCollection } from '@/utils/dataNormalization';

const silentRequestConfig = {};
const toSafeString = (value) => String(value ?? '').trim();

const normalizeSupplierOptions = (items) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const supplierId = Number(item?.id || 0);
      const supplierName = toSafeString(item?.name);
      const className = toSafeString(item?.class_name);
      const categoryName = toSafeString(item?.category_name);

      if (supplierId <= 0 || supplierName === '') {
        return null;
      }

      const classification = [className, categoryName].filter(Boolean).join(' / ');

      return {
        value: supplierId,
        label: classification !== ''
          ? `${supplierName} (${classification})`
          : supplierName,
        meta: {
          ...item,
          class_name: className,
          category_name: categoryName,
        },
      };
    })
    .filter(Boolean)
    .sort((left, right) => String(left.label).localeCompare(String(right.label), 'es'));
};

const normalizeTaxOptions = (items) => {
  return normalizeCatalogOptions(items, { useCodeInLabel: false });
};

const normalizeBasicSelectorOptions = (items) => {
  return normalizeCatalogOptions(items, { useCodeInLabel: false });
};

const normalizeSalesOptions = (items, { resolveCompanyIds = false } = {}) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const saleId = Number(item?.id || item?.sale_id || 0);
      const name = toSafeString(item?.name || item?.sale_name || item?.business_name || item?.folio || item?.code);

      if (saleId <= 0 || name === '') {
        return null;
      }

      const customerName = toSafeString(item?.company_name || item?.customer_name || item?.customer);
      const label = customerName !== '' ? `${name} (${customerName})` : name;

      if (!resolveCompanyIds) {
        return {
          value: saleId,
          label,
          meta: item,
        };
      }

      const resolvedCompanyId = Number(
        item?.company_id
        || item?.customer_id
        || item?.company?.id
        || item?.customer?.id
        || 0
      );

      return {
        value: saleId,
        label,
        meta: {
          ...item,
          company_id: resolvedCompanyId,
          customer_id: resolvedCompanyId,
        },
      };
    })
    .filter(Boolean)
    .sort((left, right) => String(left.label).localeCompare(String(right.label), 'es'));
};

const mergeCatalogOptionsByValue = (...groups) => {
  const merged = [];
  const seen = new Set();

  groups.forEach((items) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      const value = String(item?.value || '').trim();
      if (value === '' || seen.has(value)) {
        return;
      }

      seen.add(value);
      merged.push(item);
    });
  });

  return merged;
};

const normalizePaymentPeriodOptions = (items) => {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const code = toSafeString(item?.code).toLowerCase();
      const name = toSafeString(item?.name);

      if (code === '' || name === '') {
        return null;
      }

      return {
        value: code,
        label: name,
        meta: {
          ...item,
          code,
          name,
        },
      };
    })
    .filter(Boolean);
};

export const fetchExpenseFeeModalCatalogs = async ({
  includeCollaborators = false,
  resolveSalesCompanyIds = false,
} = {}) => {
  const requests = [
    ['currencies', currenciesService.selectors({}, silentRequestConfig)],
    ...(includeCollaborators ? [['collaborators', collaboratorsService.selectors({}, silentRequestConfig)]] : []),
    ['suppliers', suppliersService.selectors({}, silentRequestConfig)],
    ['customers', customersService.selectors({}, silentRequestConfig)],
    ['sales', salesService.index({ per_page: 1000, is_open: 1 }, silentRequestConfig)],
    ['flatRateSales', salesFlatRatesService.businesses({ per_page: 1000, is_open: 1 }, silentRequestConfig)],
    ['multiCustomerSales', salesMultiCustomerService.index({ per_page: 1000, is_open: 1 }, silentRequestConfig)],
    ['areas', areasService.index(silentRequestConfig)],
    ['tags', tagsService.index({}, silentRequestConfig)],
    ['taxes', taxesService.selectors({}, silentRequestConfig)],
    ['taxGroups', taxesService.groupSelectors({}, silentRequestConfig)],
    ['periodicities', generalCatalogsService.getPaymentPeriods(silentRequestConfig)],
  ];

  const results = await Promise.allSettled(requests.map(([, request]) => request));

  const collections = {};

  requests.forEach(([key], index) => {
    const result = results[index];
    collections[key] = result?.status === 'fulfilled'
      ? extractCollection(result.value)
      : [];
  });

  const normalizedStandardSales = normalizeSalesOptions(collections.sales, {
    resolveCompanyIds: resolveSalesCompanyIds,
  });
  const normalizedFlatRateSales = normalizeSalesOptions(collections.flatRateSales, {
    resolveCompanyIds: resolveSalesCompanyIds,
  });
  const normalizedMultiCustomerSales = normalizeSalesOptions(collections.multiCustomerSales, {
    resolveCompanyIds: resolveSalesCompanyIds,
  });

  return {
    currencies: normalizeCurrencyOptions(collections.currencies),
    ...(includeCollaborators
      ? { collaborators: normalizeBasicSelectorOptions(collections.collaborators) }
      : {}),
    suppliers: normalizeSupplierOptions(collections.suppliers),
    customers: normalizeBasicSelectorOptions(collections.customers),
    sales: mergeCatalogOptionsByValue(
      normalizedStandardSales,
      normalizedFlatRateSales,
      normalizedMultiCustomerSales,
    ),
    areas: normalizeBasicSelectorOptions(collections.areas),
    tags: normalizeBasicSelectorOptions(collections.tags),
    taxes: normalizeTaxOptions(collections.taxes),
    taxGroups: normalizeTaxOptions(collections.taxGroups),
    periodicities: normalizePaymentPeriodOptions(collections.periodicities),
  };
};
